import { BehaviorSubject, from, Observable, Subscription } from 'rxjs';

import {
    ChatActivity,
    ChatFileMessagePayload,
    ChatFileUploadStatus,
    ChatHistoryPageQuery,
    ChatMessage,
    ChatOutgoingMessage,
    ChatPageHasMore,
    ChatSessionState,
    ChatTranscribingPayload,
} from 'src/app/cyn/chat-module/types/chat-session.types';
import { BaseChatSession } from 'src/app/cyn/chat-module/core/sessions/base-chat.session';
import { AiChatAttachmentsService } from 'src/app/services/ai-chat-attachments.service';
import { AiChatAttachmentUploadResult } from 'src/app/models/ai-chat-attachment.model';
import { AiChatSessionStateStore } from 'src/app/state/ai-chat-session-state.store';
import { AiChatAlertService } from '../../services/ai-chat-alert.service';
import { ChatAgentSessionProvider } from '../providers/chat-agent-session.provider';
import {
    ServerEvent,
    WsDeltaEvent,
    WsErrorEvent,
    WsHistoryEvent,
    WsHistoryMessage,
    WsMessageEndEvent,
    WsMessageStartEvent,
    WsToolEndEvent,
    WsToolStartEvent,
    WsUserMessageEvent,
} from '../ws/agent-ws.types';
import {
    getHistoryCursorId,
    historyQueriesMatch,
    isTailHistoryQuery,
    normalizeHistoryQuery,
} from '../utils/chat-pagination';
const TRANSCRIBING_LABEL = 'Расшифровка...';

interface ToolActivityItem {
    messageId: string;
    runId: string;
    label: string;
}

export interface AgentChatSessionOptions {
    initialHistoryQuery?: ChatHistoryPageQuery;
    stateStore?: AiChatSessionStateStore;
}

/**
 * Реальная сессия чата с AI-агентом.
 * Дружит `ChatAgentSessionProvider` с ядром чатов и хранит UI-состояние сессии.
 */
export class AgentChatSession extends BaseChatSession {
    /** @inheritdoc */
    public override readonly id: string;

    /** @inheritdoc */
    public override readonly title: string;

    /** @inheritdoc */
    public override readonly messages$: Observable<ChatMessage[]>;

    /** @inheritdoc */
    public override readonly hasMore$: Observable<ChatPageHasMore>;

    /** @inheritdoc */
    public override readonly isLoadingMore$: Observable<ChatPageHasMore>;

    /** @inheritdoc */
    public override readonly firstUnreadId$: Observable<string | null>;

    /** @inheritdoc */
    public override readonly unreadCount$: Observable<number>;

    /** @inheritdoc */
    public override readonly activity$: Observable<ChatActivity | null>;

    /** @inheritdoc */
    public override readonly voiceInputLocked$: Observable<boolean>;

    /** @inheritdoc */
    public override readonly state$: Observable<ChatSessionState>;

    private readonly messagesSubject = new BehaviorSubject<ChatMessage[]>([]);
    private readonly hasMoreSubject = new BehaviorSubject<ChatPageHasMore>({ before: false, after: false });
    private readonly isLoadingMoreSubject = new BehaviorSubject<ChatPageHasMore>({ before: false, after: false });
    private readonly firstUnreadIdSubject = new BehaviorSubject<string | null>(null);
    private readonly unreadCountSubject = new BehaviorSubject<number>(0);
    private readonly activitySubject = new BehaviorSubject<ChatActivity | null>(null);
    private readonly voiceInputLockedSubject = new BehaviorSubject(false);
    private readonly stateSubject = new BehaviorSubject<ChatSessionState>('connecting');

    private readonly provider: ChatAgentSessionProvider;
    private readonly attachmentsService: AiChatAttachmentsService;
    private readonly alertService: AiChatAlertService;
    private readonly stateStore?: AiChatSessionStateStore;
    private readonly subscription: Subscription;
    private activeStreamingMessageId: string | null = null;
    private toolActivityStack: ToolActivityItem[] = [];
    /** id optimistic-сообщения, ожидающего расшифровку голосового ввода. */
    private pendingVoiceMessageId: string | null = null;
    /** Держим микрофон скрытым после голоса до конца ответа агента (без мигания между STT и «Думает»). */
    private awaitingAgentAfterVoice = false;
    private historyQuery: ChatHistoryPageQuery = {};
    private inFlightHistoryQuery: ChatHistoryPageQuery | null = null;
    private pendingOutgoing: ChatOutgoingMessage | null = null;

    public constructor(
        title: string,
        provider: ChatAgentSessionProvider,
        attachmentsService: AiChatAttachmentsService,
        alertService: AiChatAlertService,
        options: AgentChatSessionOptions = {},
    ) {
        super();
        this.id = provider.id;
        this.title = title;
        this.messages$ = this.messagesSubject.asObservable();
        this.hasMore$ = this.hasMoreSubject.asObservable();
        this.isLoadingMore$ = this.isLoadingMoreSubject.asObservable();
        this.firstUnreadId$ = this.firstUnreadIdSubject.asObservable();
        this.unreadCount$ = this.unreadCountSubject.asObservable();
        this.activity$ = this.activitySubject.asObservable();
        this.voiceInputLocked$ = this.voiceInputLockedSubject.asObservable();
        this.state$ = this.stateSubject.asObservable();
        this.provider = provider;
        this.attachmentsService = attachmentsService;
        this.alertService = alertService;
        this.stateStore = options.stateStore;
        this.historyQuery = normalizeHistoryQuery(options.initialHistoryQuery);
        this.subscription = new Subscription();
        this.subscription.add(this.provider.events$.subscribe(event => this.applyServerEvent(event)));
        this.subscription.add(this.provider.connectionState$.subscribe(state => this.stateSubject.next(state)));
    }

    /** Отправить сообщение агенту. Поддерживаются типы text, voice и file. */
    public override send(message: ChatOutgoingMessage): void {
        if (!this.ensureTailForSend()) {
            this.pendingOutgoing = message;
            return;
        }

        if (message.type === 'file') {
            void this.sendFileMessage(message);
            return;
        }

        if (message.type === 'text') {
            if (!message.text?.trim()) {
                return;
            }

            const outgoing: ChatMessage = {
                id: `local-${Date.now()}`,
                direction: 'outgoing',
                type: 'text',
                text: message.text,
                createdAt: new Date(),
                deliveryStatus: 'sent',
            };

            this.messagesSubject.next([...this.messagesSubject.getValue(), outgoing]);
            this.provider.sendMessage(message.text);
            return;
        }

        if (message.type === 'voice') {
            void this.sendVoiceMessage(message);
        }
    }

    private async sendVoiceMessage(message: Extract<ChatOutgoingMessage, { type: 'voice' }>): Promise<void> {
        const messageId = `local-${Date.now()}`;
        const files = message.files ?? [];
        const hasFiles = files.length > 0;

        this.pendingVoiceMessageId = messageId;
        this.awaitingAgentAfterVoice = true;

        if (hasFiles) {
            const outgoing: ChatMessage<string, ChatFileMessagePayload> = {
                id: messageId,
                direction: 'outgoing',
                type: 'file',
                text: TRANSCRIBING_LABEL,
                createdAt: new Date(),
                deliveryStatus: 'sending',
                payload: {
                    transcribing: true,
                    files: files.map((file: File) => ({
                        name: file.name,
                        size: file.size,
                        uploadStatus: 'uploading',
                        uploadProgress: 0,
                    })),
                },
            };

            this.messagesSubject.next([...this.messagesSubject.getValue(), outgoing]);
            this.syncVoiceInputLocked();

            try {
                const uploadedAttachments: { fileId: string }[] = [];

                for (let index = 0; index < files.length; index++) {
                    const result = await this.uploadSingleFile(messageId, index, files[index]);
                    uploadedAttachments.push({ fileId: result.fileId });
                }

                this.patchFileMessage(messageId, (current) => ({
                    ...current,
                    payload: {
                        transcribing: true,
                        files: (current.payload?.files ?? []).map((item) => ({
                            ...item,
                            uploadStatus: 'done' as const,
                            uploadProgress: 100,
                        })),
                    },
                }));

                if (this.pendingVoiceMessageId !== messageId) {
                    return;
                }

                this.provider.sendAudio(from(message.audio.arrayBuffer()), uploadedAttachments);
            } catch (error) {
                this.clearPendingVoicePlaceholder();
                this.alertService.show(error instanceof Error ? error.message : 'Не удалось загрузить файл.');
            }

            return;
        }

        const outgoing: ChatMessage<string, ChatTranscribingPayload> = {
            id: messageId,
            direction: 'outgoing',
            type: 'text',
            text: TRANSCRIBING_LABEL,
            createdAt: new Date(),
            deliveryStatus: 'sending',
            payload: { transcribing: true },
        };

        this.messagesSubject.next([...this.messagesSubject.getValue(), outgoing]);
        this.syncVoiceInputLocked();
        this.provider.sendAudio(from(message.audio.arrayBuffer()));
    }

    private async sendFileMessage(message: Extract<ChatOutgoingMessage, { type: 'file' }>): Promise<void> {
        if (!message.files.length) {
            return;
        }

        const text = message.text?.trim() ?? '';
        const messageId = `local-${Date.now()}`;
        const payload: ChatFileMessagePayload = {
            files: message.files.map((file: File) => ({
                name: file.name,
                size: file.size,
                uploadStatus: 'uploading',
                uploadProgress: 0,
            })),
        };

        const outgoing: ChatMessage<string, ChatFileMessagePayload> = {
            id: messageId,
            direction: 'outgoing',
            type: 'file',
            text: text || undefined,
            payload,
            createdAt: new Date(),
            deliveryStatus: 'sending',
        };

        this.messagesSubject.next([...this.messagesSubject.getValue(), outgoing]);
        this.activitySubject.next({ type: 'thinking' });
        this.activitySubject.next({ type: 'custom', label: 'Загрузка файла...' });

        try {
            const uploadedAttachments: { fileId: string }[] = [];

            for (let index = 0; index < message.files.length; index++) {
                const file = message.files[index];
                const result = await this.uploadSingleFile(messageId, index, file);
                uploadedAttachments.push({ fileId: result.fileId });
            }

            this.patchFileMessage(messageId, (current) => ({
                ...current,
                deliveryStatus: 'sent',
                payload: {
                    files: (current.payload?.files ?? []).map((item) => ({
                        ...item,
                        uploadStatus: 'done' as const,
                        uploadProgress: 100,
                    })),
                },
            }));

            this.activitySubject.next({ type: 'thinking' });
            this.provider.sendMessage(text, uploadedAttachments);
        } catch (error) {
            this.patchFileMessage(messageId, (current) => ({
                ...current,
                deliveryStatus: 'sent',
                payload: {
                    files: (current.payload?.files ?? []).map((item) =>
                        item.uploadStatus === 'done'
                            ? item
                            : { ...item, uploadStatus: 'error' as const },
                    ),
                },
            }));
            this.activitySubject.next(null);
            this.alertService.show(error instanceof Error ? error.message : 'Не удалось загрузить файл.');
        }
    }

    private uploadSingleFile(
        messageId: string,
        fileIndex: number,
        file: File,
    ): Promise<AiChatAttachmentUploadResult> {
        return new Promise((resolve, reject) => {
            let settled = false;

            const subscription = this.attachmentsService.uploadWithProgress(file).subscribe({
                next: (event) => {
                    if (event.kind === 'progress') {
                        this.updateFileProgress(messageId, fileIndex, event.progress);
                        return;
                    }

                    if (settled) {
                        return;
                    }

                    settled = true;
                    this.updateFileProgress(messageId, fileIndex, 100, event.result.fileId, 'done');
                    subscription.unsubscribe();
                    resolve(event.result);
                },
                error: () => {
                    if (settled) {
                        return;
                    }

                    settled = true;
                    this.updateFileProgress(messageId, fileIndex, 0, undefined, 'error');
                    reject(new Error(`Не удалось загрузить файл «${file.name}».`));
                },
                complete: () => {
                    if (settled) {
                        return;
                    }

                    settled = true;
                    this.updateFileProgress(messageId, fileIndex, 0, undefined, 'error');
                    reject(new Error(`Не удалось загрузить файл «${file.name}».`));
                },
            });
        });
    }

    private updateFileProgress(
        messageId: string,
        fileIndex: number,
        progress: number,
        fileId?: string,
        uploadStatus: ChatFileUploadStatus = 'uploading',
    ): void {
        this.patchFileMessage(messageId, (current) => {
            const files = [...(current.payload?.files ?? [])];
            const item = files[fileIndex];
            if (!item) {
                return current;
            }

            files[fileIndex] = {
                ...item,
                uploadProgress: progress,
                uploadStatus,
                ...(fileId ? { fileId } : {}),
            };

            return {
                ...current,
                payload: {
                    ...current.payload,
                    files,
                },
            };
        });
    }

    private patchFileMessage(
        messageId: string,
        updater: (message: ChatMessage<string, ChatFileMessagePayload>) => ChatMessage<string, ChatFileMessagePayload>,
    ): void {
        const messages = this.messagesSubject.getValue();
        const index = messages.findIndex((message) => message.id === messageId);
        if (index < 0) {
            return;
        }

        const updated = [...messages];
        updated[index] = updater(updated[index] as ChatMessage<string, ChatFileMessagePayload>);
        this.messagesSubject.next(updated);
    }

    /** Сменить страницу истории. Текущее окно заменяется целиком. */
    public override loadMore(direction: 'before' | 'after'): void {
        if (this.inFlightHistoryQuery || this.pendingVoiceMessageId || this.activeStreamingMessageId) {
            return;
        }

        const hasMore = this.hasMoreSubject.getValue();
        if (direction === 'before' && !hasMore.before) {
            return;
        }

        if (direction === 'after' && !hasMore.after) {
            return;
        }

        const cursorId = getHistoryCursorId(
            this.messagesSubject.getValue(),
            direction === 'before' ? 'first' : 'last',
        );

        if (!cursorId) {
            return;
        }

        this.requestHistory(direction === 'before' ? { before: cursorId } : { after: cursorId });
    }

    /** Сдвинуть курсор прочитанного. */
    public override markReadUpTo(messageId: string): void {
        const messages = this.messagesSubject.getValue();
        const idx = messages.findIndex(m => m.id === messageId);

        if (idx < 0) {
            return;
        }

        const unread = messages.slice(idx + 1).filter(m => m.direction === 'incoming').length;
        this.unreadCountSubject.next(unread);

        if (unread === 0) {
            this.firstUnreadIdSubject.next(null);
        }
    }

    /** Уведомление о наборе — не поддерживается протоколом агента. */
    public override notifyTyping(_isTyping: boolean): void {
        // протокол агента не поддерживает typing notifications
    }

    /** Прервать текущую генерацию ответа агента. */
    public override cancel(): void {
        this.awaitingAgentAfterVoice = false;
        this.syncVoiceInputLocked();
        this.provider.cancel();
    }

    /** Закрыть сессию и освободить ресурсы. */
    public override destroy(): void {
        this.subscription.unsubscribe();
        this.stateSubject.next('closed');
        this.messagesSubject.complete();
        this.hasMoreSubject.complete();
        this.isLoadingMoreSubject.complete();
        this.firstUnreadIdSubject.complete();
        this.unreadCountSubject.complete();
        this.activitySubject.complete();
        this.voiceInputLockedSubject.complete();
        this.stateSubject.complete();
    }

    /** Переводит сессию в активное подключенное состояние. */
    public activate(): void {
        this.provider.setConnectionState('connected');
        this.persistMessagesPage();
        this.requestHistory(this.historyQuery, true);
    }

    /** Отмечает потерю соединения как ошибку уровня сессии. */
    public markConnectionError(): void {
        this.clearPendingVoicePlaceholder();
        this.activitySubject.next(null);
        this.syncVoiceInputLocked();
        this.stateSubject.next('error');
    }

    /** Применить очередное событие протокола к состоянию открытой сессии. */
    private applyServerEvent(event: ServerEvent): void {
        if (event.kind !== 'history' && event.kind !== 'error' && !this.isLiveWindow()) {
            return;
        }

        switch (event.kind) {
            case 'history':
                this.applyHistory(event);
                break;

            case 'message_start':
                this.handleMessageStart(event);
                break;

            case 'delta':
                this.handleDelta(event);
                break;

            case 'tool_start':
                this.handleToolStart(event);
                break;

            case 'tool_end':
                this.handleToolEnd(event);
                break;

            case 'message_end':
                this.handleMessageEnd(event);
                break;

            case 'message_cancel':
                this.finalizeStreamingMessage(event.messageId);
                break;

            case 'user_message':
                this.handleUserMessage(event);
                break;

            case 'error':
                this.handleError(event);
                break;
        }
    }

    private handleUserMessage(event: WsUserMessageEvent): void {
        const messages = this.messagesSubject.getValue();
        const pendingVoiceIndex = this.findPendingVoiceMessageIndex(messages);

        if (pendingVoiceIndex >= 0) {
            const alreadyPresent = messages.some((message, index) =>
                index !== pendingVoiceIndex && this.isSameOutgoingText(message, event.text),
            );

            if (alreadyPresent) {
                this.pendingVoiceMessageId = null;
                this.messagesSubject.next(messages.filter((_, index) => index !== pendingVoiceIndex));
                this.syncVoiceInputLocked();
                return;
            }

            const updated = [...messages];
            const pending = updated[pendingVoiceIndex];
            const pendingFiles = (pending.payload as ChatFileMessagePayload | undefined)?.files;
            const hasFiles = pending.type === 'file' && Boolean(pendingFiles?.length);

            updated[pendingVoiceIndex] = {
                id: pending.id,
                direction: 'outgoing',
                type: hasFiles ? 'file' : 'text',
                text: this.normalizeOutgoingText(event.text),
                createdAt: pending.createdAt,
                deliveryStatus: 'delivered',
                ...(hasFiles
                    ? {
                        payload: {
                            files: pendingFiles!.map((item) => ({
                                ...item,
                                uploadStatus: 'done' as const,
                                uploadProgress: 100,
                            })),
                        },
                    }
                    : {}),
            };
            this.pendingVoiceMessageId = null;
            this.messagesSubject.next(updated);
            this.syncVoiceInputLocked();
            return;
        }

        const optimisticMessageIndex = this.findOptimisticOutgoingMessageIndex(event.text);

        if (optimisticMessageIndex >= 0) {
            const updated = [...messages];
            updated[optimisticMessageIndex] = {
                ...updated[optimisticMessageIndex],
                deliveryStatus: 'delivered',
            };
            this.messagesSubject.next(updated);
            return;
        }

        // История могла уже успеть подтянуть расшифровку, не дублируем её в конце списка.
        if (messages.some(message => this.isSameOutgoingText(message, event.text))) {
            return;
        }

        const message: ChatMessage = {
            id: `user-${Date.now()}`,
            direction: 'outgoing',
            type: 'text',
            text: this.normalizeOutgoingText(event.text),
            createdAt: new Date(),
            deliveryStatus: 'delivered',
        };

        this.messagesSubject.next([...messages, message]);
    }

    private handleMessageStart(event: WsMessageStartEvent): void {
        this.activeStreamingMessageId = event.messageId;
        this.toolActivityStack = [];
        this.updateActivity();
    }

    private handleDelta(event: WsDeltaEvent): void {
        if (this.activeStreamingMessageId !== event.messageId) {
            return;
        }

        const messages = this.getMessagesWithStreamingPlaceholder(event.messageId);
        const idx = messages.findIndex(m => m.id === event.messageId);

        if (idx < 0) {
            return;
        }

        const updated = [...messages];
        updated[idx] = { ...updated[idx], text: (updated[idx].text ?? '') + event.text };
        this.messagesSubject.next(updated);
    }

    private handleMessageEnd(event: WsMessageEndEvent): void {
        if (this.activeStreamingMessageId !== event.messageId) {
            return;
        }

        this.activeStreamingMessageId = null;
        this.toolActivityStack = [];
        this.updateActivity();

        const shouldCreateMessage = Boolean(event.text || event.blocks?.length);
        const messages = shouldCreateMessage
            ? this.getMessagesWithStreamingPlaceholder(event.messageId)
            : this.messagesSubject.getValue();
        const idx = messages.findIndex(m => m.id === event.messageId);

        if (idx < 0) {
            return;
        }

        const updated = [...messages];
        const finalText = event.text || updated[idx].text || '';

        if (!finalText && !(event.blocks?.length)) {
            updated.splice(idx, 1);
            this.messagesSubject.next(updated);
            return;
        }

        updated[idx] = { ...updated[idx], text: finalText, deliveryStatus: 'delivered' };

        const blockMessages: ChatMessage[] = (event.blocks ?? []).map((block, i) => ({
            id: `${event.messageId}_block_${i}`,
            direction: 'incoming' as const,
            type: block.name,
            payload: block.data,
            createdAt: new Date(),
        }));

        this.messagesSubject.next([...updated, ...blockMessages]);
    }

    private handleError(event: WsErrorEvent): void {
        if (event.messageId && this.activeStreamingMessageId === event.messageId) {
            this.activeStreamingMessageId = null;
            this.toolActivityStack = [];
        }

        if (event.code === '4001') {
            this.provider.setConnectionState('error');
        }

        if (event.code === 'rate_limit') {
            this.alertService.show(
                'Достигнут дневной лимит запросов. Попробуйте позднее или обратитесь в техническую поддержку для повышения лимитов на использование ассистента.',
            );
        }

        this.clearPendingVoicePlaceholder();

        this.updateActivity();
    }

    private applyHistory(event: WsHistoryEvent): void {
        const requested = this.inFlightHistoryQuery ?? this.historyQuery;

        if (this.inFlightHistoryQuery && !historyQueriesMatch(this.inFlightHistoryQuery, event)) {
            return;
        }

        this.inFlightHistoryQuery = null;
        this.isLoadingMoreSubject.next({ before: false, after: false });

        if (!event.messages.length && !isTailHistoryQuery(requested)) {
            this.requestHistory({});
            return;
        }

        const mapped = event.messages.flatMap(message => this.mapHistoryMessage(message));
        const reachedTail = isTailHistoryQuery(requested) || (Boolean(requested.after) && !event.hasMore);
        const wasAlreadyTail = isTailHistoryQuery(this.historyQuery);

        if (reachedTail) {
            const existing = this.messagesSubject.getValue();
            const keep = wasAlreadyTail
                ? existing
                : existing.filter(message => this.isOptimisticOutgoingMessage(message) || this.isTranscribingMessage(message));
            this.messagesSubject.next(this.mergeInitialHistory(mapped, keep));
        } else {
            this.messagesSubject.next(mapped);
        }

        this.historyQuery = reachedTail ? {} : requested;
        this.hasMoreSubject.next(this.resolveHistoryHasMore(requested, Boolean(event.hasMore)));
        this.persistMessagesPage();

        if (this.pendingOutgoing && !isTailHistoryQuery(this.historyQuery)) {
            this.requestHistory({});
            return;
        }

        this.flushPendingOutgoing();
    }

    private requestHistory(query: ChatHistoryPageQuery, fromActivate = false): void {
        const normalized = normalizeHistoryQuery(query);
        this.inFlightHistoryQuery = normalized;

        const jumpingToTail = isTailHistoryQuery(normalized) && !fromActivate;
        this.isLoadingMoreSubject.next({
            before: Boolean(normalized.before),
            after: Boolean(normalized.after) || jumpingToTail,
        });

        this.provider.loadHistory(normalized);
    }

    private resolveHistoryHasMore(requested: ChatHistoryPageQuery, hasMore: boolean): ChatPageHasMore {
        if (requested.before) {
            return { before: hasMore, after: true };
        }

        if (requested.after) {
            return { before: true, after: hasMore };
        }

        return { before: hasMore, after: false };
    }

    private ensureTailForSend(): boolean {
        if (isTailHistoryQuery(this.historyQuery)
            && (this.inFlightHistoryQuery === null || isTailHistoryQuery(this.inFlightHistoryQuery))) {
            return true;
        }

        if (!this.inFlightHistoryQuery || !isTailHistoryQuery(this.inFlightHistoryQuery)) {
            this.requestHistory({});
        }

        return false;
    }

    private flushPendingOutgoing(): void {
        if (!this.pendingOutgoing || !isTailHistoryQuery(this.historyQuery) || this.inFlightHistoryQuery) {
            return;
        }

        const message = this.pendingOutgoing;
        this.pendingOutgoing = null;
        this.send(message);
    }

    private isLiveWindow(): boolean {
        return isTailHistoryQuery(this.historyQuery)
            && (this.inFlightHistoryQuery === null || isTailHistoryQuery(this.inFlightHistoryQuery));
    }

    private persistMessagesPage(): void {
        this.stateStore?.patchState({
            ui: {
                messagesPage: {
                    sessionId: this.id,
                    ...this.historyQuery,
                },
            },
        });
    }

    private mapHistoryMessage(msg: WsHistoryMessage): ChatMessage[] {
        const hasAttachments = Boolean(msg.attachments?.length);
        const files = (msg.attachments ?? []).map((attachment) => ({
            name: attachment.name,
            size: attachment.size,
            fileId: attachment.fileId,
            uploadStatus: 'done' as const,
            uploadProgress: 100,
        }));

        const primary: ChatMessage = {
            id: msg.messageId,
            direction: msg.role === 'user' ? 'outgoing' : 'incoming',
            type: hasAttachments ? 'file' : 'text',
            text: this.normalizeOutgoingText(msg.text),
            createdAt: new Date(msg.timestamp),
            deliveryStatus: msg.role === 'user' ? 'delivered' : undefined,
            payload: hasAttachments ? { files } : undefined,
        };

        const blocks: ChatMessage[] = (msg.blocks ?? []).map((block, i) => ({
            id: `${msg.messageId}_block_${i}`,
            direction: 'incoming' as const,
            type: block.name,
            payload: block.data,
            createdAt: new Date(msg.timestamp),
        }));

        return [primary, ...blocks];
    }

    private finalizeStreamingMessage(messageId: string): void {
        if (this.activeStreamingMessageId === messageId) {
            this.activeStreamingMessageId = null;
        }

        this.toolActivityStack = this.toolActivityStack.filter(item => item.messageId !== messageId);
        this.updateActivity();

        const messages = this.messagesSubject.getValue();
        const idx = messages.findIndex(m => m.id === messageId);

        if (idx < 0) {
            return;
        }

        const updated = [...messages];
        updated[idx] = { ...updated[idx], deliveryStatus: 'delivered' };
        this.messagesSubject.next(updated);
    }

    private getMessagesWithStreamingPlaceholder(messageId: string): ChatMessage[] {
        const existingMessages = this.messagesSubject.getValue();

        if (existingMessages.some(message => message.id === messageId)) {
            return existingMessages;
        }

        const streamingMessage: ChatMessage = {
            id: messageId,
            direction: 'incoming',
            type: 'text',
            text: '',
            createdAt: new Date(),
        };

        const updatedMessages = [...existingMessages, streamingMessage];
        this.messagesSubject.next(updatedMessages);
        return updatedMessages;
    }

    private handleToolStart(event: WsToolStartEvent): void {
        if (this.activeStreamingMessageId !== event.messageId) {
            return;
        }

        const label = event.label?.trim();

        if (!label) {
            return;
        }

        this.toolActivityStack = [
            ...this.toolActivityStack,
            {
                messageId: event.messageId,
                runId: event.runId,
                label,
            },
        ];
        this.updateActivity();
    }

    private handleToolEnd(event: WsToolEndEvent): void {
        if (this.activeStreamingMessageId !== event.messageId) {
            return;
        }

        const stackIndex = this.findToolActivityIndex(event.runId, event.messageId);

        if (stackIndex < 0) {
            return;
        }

        this.toolActivityStack = this.toolActivityStack.filter((_, index) => index !== stackIndex);
        this.updateActivity();
    }

    private updateActivity(): void {
        const currentToolActivity = this.toolActivityStack[this.toolActivityStack.length - 1];

        if (currentToolActivity) {
            this.activitySubject.next({ type: 'custom', label: currentToolActivity.label });
            this.syncVoiceInputLocked();
            return;
        }

        if (this.activeStreamingMessageId) {
            this.activitySubject.next({ type: 'thinking' });
            this.syncVoiceInputLocked();
            return;
        }

        this.activitySubject.next(null);
        this.awaitingAgentAfterVoice = false;
        this.syncVoiceInputLocked();
    }

    private syncVoiceInputLocked(): void {
        const locked = this.pendingVoiceMessageId !== null
            || this.awaitingAgentAfterVoice
            || this.activitySubject.getValue() !== null;

        if (this.voiceInputLockedSubject.getValue() !== locked) {
            this.voiceInputLockedSubject.next(locked);
        }
    }

    private findToolActivityIndex(runId: string, messageId: string): number {
        for (let index = this.toolActivityStack.length - 1; index >= 0; index--) {
            const item = this.toolActivityStack[index];

            if (item.runId === runId && item.messageId === messageId) {
                return index;
            }
        }

        return -1;
    }

    private mergeInitialHistory(historyMessages: ChatMessage[], existingMessages: ChatMessage[]): ChatMessage[] {
        if (!existingMessages.length) {
            return historyMessages;
        }

        const notLoadedYetMessages = existingMessages.filter(message => this.shouldKeepExistingMessage(message, historyMessages));

        if (this.pendingVoiceMessageId
            && !notLoadedYetMessages.some(message => message.id === this.pendingVoiceMessageId)) {
            // История уже содержит расшифровку, placeholder больше не нужен.
            this.pendingVoiceMessageId = null;
            this.syncVoiceInputLocked();
        }

        if (!notLoadedYetMessages.length) {
            return historyMessages;
        }

        return [...historyMessages, ...notLoadedYetMessages];
    }

    private findOptimisticOutgoingMessageIndex(text: string): number {
        const messages = this.messagesSubject.getValue();

        for (let index = messages.length - 1; index >= 0; index--) {
            const message = messages[index];

            if (this.isOptimisticOutgoingMessage(message) && message.text === text) {
                return index;
            }
        }

        return -1;
    }

    private findPendingVoiceMessageIndex(messages: ChatMessage[]): number {
        if (!this.pendingVoiceMessageId) {
            return -1;
        }

        return messages.findIndex(message => message.id === this.pendingVoiceMessageId);
    }

    private clearPendingVoicePlaceholder(): void {
        if (!this.pendingVoiceMessageId && !this.awaitingAgentAfterVoice) {
            return;
        }

        const pendingId = this.pendingVoiceMessageId;
        this.pendingVoiceMessageId = null;
        this.awaitingAgentAfterVoice = false;
        this.syncVoiceInputLocked();

        if (!pendingId) {
            return;
        }

        this.messagesSubject.next(
            this.messagesSubject.getValue().filter(message => message.id !== pendingId),
        );
    }

    private isOptimisticOutgoingMessage(message: ChatMessage): boolean {
        return message.direction === 'outgoing'
            && typeof message.id === 'string'
            && message.id.startsWith('local-');
    }

    private isTranscribingMessage(message: ChatMessage): boolean {
        const payload = message.payload as { transcribing?: boolean } | undefined;
        return Boolean(payload?.transcribing);
    }

    private isSameOutgoingText(message: ChatMessage, text: string): boolean {
        return message.direction === 'outgoing'
            && (message.type === 'text' || message.type === 'file')
            && this.normalizeOutgoingText(message.text) === this.normalizeOutgoingText(text)
            && !this.isTranscribingMessage(message);
    }

    private normalizeOutgoingText(text: string | undefined | null): string | undefined {
        const value = text?.trim();
        return value ? value : undefined;
    }

    private getOutgoingFileIds(message: ChatMessage): string[] {
        const files = (message.payload as ChatFileMessagePayload | undefined)?.files ?? [];
        return files
            .map((file) => file.fileId)
            .filter((fileId): fileId is string => Boolean(fileId))
            .sort();
    }

    private haveSameOutgoingAttachments(left: ChatMessage, right: ChatMessage): boolean {
        const leftIds = this.getOutgoingFileIds(left);
        const rightIds = this.getOutgoingFileIds(right);

        return leftIds.length > 0
            && leftIds.length === rightIds.length
            && leftIds.every((fileId, index) => fileId === rightIds[index]);
    }

    private areSameOutgoingMessage(left: ChatMessage, right: ChatMessage): boolean {
        if (left.direction !== 'outgoing'
            || right.direction !== 'outgoing'
            || this.isTranscribingMessage(left)
            || this.isTranscribingMessage(right)) {
            return false;
        }

        if (this.haveSameOutgoingAttachments(left, right)) {
            return true;
        }

        return left.type === right.type
            && this.normalizeOutgoingText(left.text) === this.normalizeOutgoingText(right.text)
            && this.getOutgoingFileIds(left).length === 0
            && this.getOutgoingFileIds(right).length === 0;
    }

    private shouldKeepExistingMessage(message: ChatMessage, historyMessages: ChatMessage[]): boolean {
        if (this.isTranscribingMessage(message)) {
            if (this.getOutgoingFileIds(message).length > 0
                && historyMessages.some((historyMessage) => this.haveSameOutgoingAttachments(historyMessage, message))) {
                return false;
            }

            // Если сервер уже вернул пользовательское сообщение после старта записи, placeholder убираем.
            return !historyMessages.some(historyMessage =>
                historyMessage.direction === 'outgoing'
                && Boolean(this.normalizeOutgoingText(historyMessage.text))
                && historyMessage.text !== TRANSCRIBING_LABEL
                && historyMessage.createdAt.getTime() >= message.createdAt.getTime(),
            );
        }

        if (this.isOptimisticOutgoingMessage(message)) {
            return !historyMessages.some(historyMessage => this.areSameOutgoingMessage(historyMessage, message));
        }

        // user-* после голосового ввода не совпадает с server messageId, дедупим по тексту/вложениям.
        if (message.direction === 'outgoing'
            && typeof message.id === 'string'
            && message.id.startsWith('user-')) {
            return !historyMessages.some(historyMessage => this.areSameOutgoingMessage(historyMessage, message));
        }

        return !historyMessages.some(historyMessage => historyMessage.id === message.id);
    }
}
